import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import StarRating from './StarRating';
import { Review } from '../../types/review';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContainer = styled.div`
  background: rgba(26, 35, 46, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid #00d4ff;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
  position: relative;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  position: sticky;
  top: 0;
  background: rgba(26, 35, 46, 0.98);
  z-index: 1;
  border-radius: 16px 16px 0 0;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AuthorName = styled.h2`
  color: #00d4ff;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
`;

const ReviewDate = styled.span`
  color: #a0a9b8;
  font-size: 0.85rem;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #00d4ff;
    color: #00d4ff;
    transform: scale(1.1);
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const VerifiedBadge = styled.span`
  background: rgba(0, 255, 100, 0.1);
  border: 1px solid rgba(0, 255, 100, 0.3);
  color: #00ff64;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const VerificationDetails = styled.span`
  color: #a0a9b8;
  font-size: 0.8rem;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const TargetSection = styled.div`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const TargetLabel = styled.span`
  color: #a0a9b8;
  font-size: 0.85rem;
  display: block;
  margin-bottom: 8px;
`;

const TargetLink = styled(Link)`
  color: #00d4ff;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ReviewTextSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  color: #00d4ff;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReviewText = styled.p`
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
  white-space: pre-wrap;
`;

const NoText = styled.p`
  color: #666;
  font-style: italic;
  margin: 0;
`;

const MediaSection = styled.div`
  margin-bottom: 24px;
`;

const PhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

const PhotoThumbnail = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 212, 255, 0.2);

  &:hover {
    border-color: #00d4ff;
    transform: scale(1.02);
  }
`;

const VideoContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  width: 280px;
  height: 160px;
  border: 1px solid rgba(0, 212, 255, 0.2);
`;

const YouTubeEmbed = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
`;

const FullImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const ImageCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #fff;
  }
`;

const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const getTargetTypeLabel = (type: string): string => {
  switch (type) {
    case 'field': return 'Field';
    case 'map': return 'Map';
    case 'mission': return 'Mission';
    case 'referee': return 'Referee';
    case 'game': return 'Game';
    default: return type;
  }
};

const getTargetLink = (type: string, id: string): string => {
  switch (type) {
    case 'field': return `/fields/${id}`;
    case 'map': return `/fields/maps/${id}`;
    case 'mission': return `/operations/${id}`;
    case 'referee': return `/referees/${id}`;
    case 'game': return `/missions/${id}`;
    default: return '#';
  }
};

interface ReviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: Review | null;
}

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({
  isOpen,
  onClose,
  review
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (!isOpen || !review) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const videoId = review.youtubeVideoUrl ? getYouTubeVideoId(review.youtubeVideoUrl) : null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <Overlay onClick={handleOverlayClick}>
        <ModalContainer>
          <ModalHeader>
            <HeaderTop>
              <AuthorInfo>
                <AuthorName>{review.userName}</AuthorName>
                <ReviewDate>{formatDate(review.createdAt)}</ReviewDate>
              </AuthorInfo>
              <CloseButton onClick={onClose}>×</CloseButton>
            </HeaderTop>
            <RatingRow>
              <StarRating rating={review.rating} size="medium" showRating={true} />
              {review.isVerified && (
                <>
                  <VerifiedBadge>Verified</VerifiedBadge>
                  {review.verificationDetails && (
                    <VerificationDetails>{review.verificationDetails}</VerificationDetails>
                  )}
                </>
              )}
            </RatingRow>
          </ModalHeader>

          <ModalBody>
            {/* Review Text */}
            <ReviewTextSection>
              <SectionTitle>Review</SectionTitle>
              {review.reviewText ? (
                <ReviewText>{review.reviewText}</ReviewText>
              ) : (
                <NoText>No text review provided</NoText>
              )}
            </ReviewTextSection>

            {/* Photos */}
            {review.images && review.images.length > 0 && (
              <MediaSection>
                <SectionTitle>Photos ({review.images.length})</SectionTitle>
                <PhotosGrid>
                  {review.images.map((image, index) => (
                    <PhotoThumbnail
                      key={index}
                      src={image}
                      alt={`Review photo ${index + 1}`}
                      onClick={() => setSelectedImage(image)}
                    />
                  ))}
                </PhotosGrid>
              </MediaSection>
            )}

            {/* Video */}
            {videoId && (
              <MediaSection>
                <SectionTitle>Video</SectionTitle>
                <VideoContainer>
                  <YouTubeEmbed
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="Review Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </VideoContainer>
              </MediaSection>
            )}
          </ModalBody>
        </ModalContainer>
      </Overlay>

      {/* Full Image Modal */}
      {selectedImage && (
        <ImageModal onClick={() => setSelectedImage(null)}>
          <ImageCloseButton onClick={() => setSelectedImage(null)}>×</ImageCloseButton>
          <FullImage src={selectedImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </ImageModal>
      )}
    </>
  );
};

export default ReviewDetailModal;
